/**
 * @file Simple export button component with progress indication
 * @version 1.0.0
 * @author Claude Assistant
 * @since 2025-07-09
 */

import { useDataPortability } from '../composables/dataPortability.js';
import { useNotifications } from '../../core/errors.js';

const { ref, computed } = Vue;

export default {
  name: 'DataExportButton',

  inheritAttrs: false,

  template: `
    <button 
      class="btn" 
      :class="Object.values($attrs).length ? '': 'btn-secondary'"
      :disabled="isExporting"
      @click="handleExport"
      v-bind="$attrs"
    >
      <span v-if="isExporting" class="spinner-border spinner-border-sm me-2" role="status"></span>
      <i v-else :class="icon + ' me-2'"></i>
      {{ buttonLabel }}
    </button>
  `,

  props: {
    collectionName: {
      type: String,
      required: true,
    },
    filename: {
      type: String,
      default: null,
    },
    includeMetadata: {
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      default: 'bi-download',
    },
    label: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const { exportCollection, isExporting } = useDataPortability();
    const { success, error } = useNotifications();

    const buttonLabel = computed(() => {
      if (props.label) return props.label;
      return isExporting.value
        ? 'Exporting...'
        : `Export ${props.collectionName}`;
    });

    const defaultFilename = computed(() => {
      if (props.filename) return props.filename;
      const timestamp = new Date().toISOString().split('T')[0];
      return `${props.collectionName}-export-${timestamp}.json`;
    });

    const handleExport = async () => {
      try {
        console.log('Starting export for collection:', props.collectionName);
        const result = await exportCollection(props.collectionName, {
          filename: defaultFilename.value,
        });
        console.log('Export result:', result);

        success(
          `Exported ${result.recordCount} records from ${props.collectionName}`
        );
      } catch (err) {
        console.error('Export error:', err);
        error(`Export failed: ${err.message || 'Unknown error'}`);
      }
    };

    return {
      buttonLabel,
      handleExport,
      isExporting,
    };
  },
};
